import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { cardReadersApi, orderPaymentsApi, registersApi } from '../utils/api';
import { useLacksEntitlement } from '../contexts/EntitlementsContext';
import { formatDateInTimezone } from '../utils/dateUtils';
import { fieldLabelClass as labelClass } from './orderDetailsUI';
import { getDeviceRegisterId, getReceiptChoice, setDeviceRegisterId, setReceiptChoice } from './register/registerUtils';
import ReceiptPrinterStatus from './register/ReceiptPrinterStatus';
import useReceiptPrinterStatus from '../hooks/useReceiptPrinterStatus';
import { BanknoteIcon, CheckIcon, CopyIcon, CreditCardIcon, GiftIcon, LinkIcon, LockOpenIcon, MailIcon, MessageSquareIcon, PrinterIcon, ReceiptIcon, RefreshCwIcon, RotateCcwIcon, XIcon } from './icons/LucideIcons';

/**
 * "Take payment" on a saved staff order (audit R1; backend
 * routes/order-payments.js), for Stripe shops and shops with no card
 * processor (QA F3: cash, check, gift card and other only). Hidden for
 * Shopify shops (Twigs), for house-account and cancelled orders, and when
 * nothing is due and no payment was taken.
 *
 *   Enter card        Stripe Payment Element, card only, on the shop's
 *                     connected account; "Save card for this customer".
 *   Send pay link     one tap: Text pay link / Email pay link (only the
 *                     channels the order has; neither: Make a link to copy).
 *                     Open links are listed with Copy, Text, Email, Cancel.
 *   Charge saved card when the customer has one ("Visa ··4242"); if the bank
 *                     wants the customer to confirm, it falls back to a pay link.
 *
 *   Every card way has an Amount field, prefilled with the balance due: a
 *   smaller amount splits the payment (two cards, a card and a link, two
 *   links) and the panel reopens for what is left. Open pay links together
 *   never ask for more than is due.
 *   Gift card         (Online store 4C) enter the code, Check balance, then
 *                     Apply: up to the balance due comes off the card at once
 *                     (no Stripe). Hidden for shops known not to have the
 *                     online store; the server answers 403 for them anyway.
 *   Charge on reader  (audit R2) when the shop has a Stripe Reader S700
 *                     registered: the balance goes to the reader, the panel
 *                     waits for the card ("Waiting for card on Front counter…",
 *                     with Cancel) and polls until the webhook records it or the
 *                     decline reason shows. Reopening the order resumes the wait.
 *   Cash              (cash register on) the cash handed over and the change due,
 *                     shown big; recorded on this device's open register
 *                     ("Open the register first" when none is). The change stays
 *                     on screen until Done.
 *   Check             (cash register on) the check number and amount; in the
 *                     register's deposit, never its cash count.
 *   Other             owners and admins: "Mark paid" for money taken outside the
 *                     app (an invoice), with a reason.
 *
 * Receipts (REG2), once the shop has a receipt printer on its Bouqmark Print
 * Client: after a cash sale (and a card or check sale while the shop's
 * print_receipts setting is on) the panel asks "Print receipt" or "No
 * receipt"; the choice this device made last is offered first. A cash sale
 * opens the cash drawer by itself (the server sends the kick). The payments
 * list has Reprint receipt. The receipt printer shows online or offline.
 *
 * Stripe's webhook records the payment; after a card is confirmed the panel
 * polls until it shows. Admins see Refund on card payments.
 *
 * While the customer of an online store order is paying for it online (its
 * checkout open with a payment in flight), the panel says so instead of
 * offering any way to charge; the server refuses charges then too (409
 * CHECKOUT_IN_PROGRESS). Online store orders (4E) also show what the customer paid at checkout: the
 * payment status, total paid, tax with its rate and source, tip, the card,
 * and (admins only) the Stripe payment id with Copy. Their payment is in the
 * same ledger, so the same Refund works for them. With a discount code or a
 * gift card (4C) the block shows those too; Refund gives card payments back
 * first and puts the rest back on the gift card.
 */

// Which provider this shop takes cards with, asked once per login.
let configCache = { token: undefined, promise: null };
const currentToken = () => {
  try { return localStorage.getItem('token'); } catch (_) { return null; }
};
export function loadOrderPaymentConfig() {
  const token = currentToken();
  if (!configCache.promise || configCache.token !== token) {
    const promise = Promise.resolve()
      .then(() => orderPaymentsApi.config())
      .then((r) => (r && r.data) || { provider: null })
      .catch(() => {
        if (configCache.promise === promise) configCache = { token: undefined, promise: null };
        return { provider: null };
      });
    configCache = { token, promise };
  }
  return configCache.promise;
}
export function __resetOrderPaymentConfig() { configCache = { token: undefined, promise: null }; }

const newKey = () => {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID().replace(/-/g, '');
  } catch (_) { /* fall through */ }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const cents = (n) => Math.round(Number(n || 0) * 100);

// Disabled states use a surface step and an edge, never an opacity fade (Fable).
// Settings → Payments → Card readers uses the same buttons.
export const paymentButtonClass = {
  primary: 'inline-flex items-center justify-center gap-1.5 rounded-full border border-floral-700 bg-floral-700 px-4 py-2 font-heading text-sm text-white hover:bg-floral-800 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-200 disabled:text-gray-600',
  secondary: 'inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-300 bg-white px-4 py-2 font-heading text-sm text-gray-900 hover:bg-gray-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500',
  ghost: 'inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 font-heading text-[13px] text-floral-700 hover:bg-floral-50 disabled:cursor-not-allowed disabled:text-gray-500 [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-floral-900 [html[data-scheme=dark]:not([data-appearance=fable])_&]:disabled:text-gray-500',
};
// This panel itself (order detail rail, order form success dialog) uses the
// Forest primitives; paymentButtonClass above stays for the screens importing it.
const btn = { primary: 'btn-primary', secondary: 'btn-secondary', ghost: 'btn-ghost btn-sm' };
const inputClass = 'input-field';
const tabClass = (active) => `seg-item ${active ? 'seg-item-active' : ''}`;
const errorText = 'text-[13px] text-danger-fg';

// "Visa ··4242" → a small brand tile and "··4242" (the label is all the app
// keeps of a card: brand and last 4). Anything else (Cash, Check #12…) as is.
const BRAND_TILES = { visa: 'VISA', mastercard: 'MC', amex: 'AMEX', discover: 'DISC', diners: 'DIN', jcb: 'JCB', unionpay: 'UPAY' };
function PaymentLabel({ label, when }) {
  const m = /^(Visa|Mastercard|Amex|Discover|Diners|JCB|UnionPay) ··(\d{4})$/.exec(label || '');
  if (!m) return <span className="text-gray-900">{label || 'Card'}{when}</span>;
  return (
    <span className="flex items-center gap-2 text-gray-900">
      <span aria-hidden="true" className="grid h-[18px] w-7 place-items-center rounded-[3px] bg-gray-900 text-[8px] font-bold text-surface-alt">{BRAND_TILES[m[1].toLowerCase()]}</span>
      <span className="tabular"><span className="sr-only">{m[1]} </span>··{m[2]}{when}</span>
    </span>
  );
}

// A split payment: the amount a card path is for, typed by staff. Blank or
// the whole balance means everything due (nothing is sent to the server).
const fmtAmount = (dollars) => Number(dollars || 0).toFixed(2);
function splitAmount(text, balanceDue) {
  const dueCents = cents(balanceDue);
  const parsed = String(text).trim() === '' ? dueCents : parseDollars(text);
  const valid = parsed !== null && parsed >= 50 && parsed <= dueCents;
  return {
    cents: valid ? parsed : dueCents,
    valid,
    // what to send: null for the whole balance
    amount: valid && parsed !== dueCents ? parsed / 100 : null,
    problem: valid ? null : parsed === null ? 'Enter an amount in dollars' : parsed < 50 ? 'At least $0.50' : `At most ${money(balanceDue)}`,
  };
}

// Prefilled with the balance due, and reset when the balance changes (a
// payment landed). `prefill` can start it lower (what open pay links leave).
function useAmountText(balanceDue, prefill = null) {
  const [text, setText] = useState(() => fmtAmount(prefill ?? balanceDue));
  const last = useRef(balanceDue);
  useEffect(() => {
    if (last.current !== balanceDue) { last.current = balanceDue; setText(fmtAmount(prefill ?? balanceDue)); }
  }, [balanceDue, prefill]);
  return [text, setText];
}

function AmountField({ id, balanceDue, text, onChange, split }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[13px] font-medium text-gray-900" htmlFor={id}>Amount</label>
      <span className="flex items-center gap-2">
        <input id={id} inputMode="decimal" autoComplete="off" value={text} onChange={(e) => onChange(e.target.value)} className={`${inputClass} w-32 tabular-nums`} />
        <span className="text-[13px] text-gray-600">of {money(balanceDue)} due</span>
      </span>
      {split.problem && <span className={errorText} role="alert">{split.problem}</span>}
    </div>
  );
}

function EnterCard({ orderId, config, amount, canSaveCard, onConfirmed }) {
  const mountRef = useRef(null);
  const stripeRef = useRef(null);
  const elementsRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [save, setSave] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [amountText, setAmountText] = useAmountText(amount);
  const split = splitAmount(amountText, amount);
  const amountCents = split.cents;

  useEffect(() => {
    let cancelled = false;
    let element = null;
    (async () => {
      try {
        if (!config.publishableKey || !config.accountId) throw new Error('not configured');
        const { loadStripe } = await import('@stripe/stripe-js/pure');
        const stripe = await loadStripe(config.publishableKey, { stripeAccount: config.accountId });
        if (cancelled || !stripe || !mountRef.current) return;
        const elements = stripe.elements({
          mode: 'payment',
          amount: amountCents,
          currency: 'usd',
          paymentMethodTypes: ['card'],
          appearance: { theme: 'stripe', variables: { borderRadius: '12px' } },
        });
        element = elements.create('payment', { layout: 'tabs', wallets: { applePay: 'never', googlePay: 'never', link: 'never' } });
        element.on('ready', () => { if (!cancelled) setReady(true); });
        element.mount(mountRef.current);
        stripeRef.current = stripe;
        elementsRef.current = elements;
      } catch (_) {
        if (!cancelled) setLoadError('The card form could not load. Refresh the page and try again.');
      }
    })();
    return () => { cancelled = true; if (element) element.destroy(); elementsRef.current = null; };
  }, [config.publishableKey, config.accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The amount typed (a split) is what Stripe's form charges.
  useEffect(() => { if (elementsRef.current && amountCents > 0) elementsRef.current.update({ amount: amountCents }); }, [amountCents]);
  useEffect(() => { if (elementsRef.current) elementsRef.current.update({ setupFutureUsage: save ? 'off_session' : null }); }, [save]);

  async function pay() {
    const stripe = stripeRef.current;
    const elements = elementsRef.current;
    if (!stripe || !elements || !split.valid) return;
    setBusy(true);
    setError(null);
    const { error: invalid } = await elements.submit();
    if (invalid) { setError(invalid.message); setBusy(false); return; }
    let started;
    try {
      started = await orderPaymentsApi.cardIntent(orderId, { attempt_key: newKey(), save_card: save, ...(split.amount !== null ? { amount: split.amount } : {}) });
    } catch (err) {
      setError(err.message || 'The payment could not start.');
      setBusy(false);
      return;
    }
    const { error: declined } = await stripe.confirmPayment({
      elements,
      clientSecret: started.data.clientSecret,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });
    if (declined) { setError(declined.message || 'The card was not charged.'); setBusy(false); return; }
    setBusy(false);
    onConfirmed(started.data.paymentId);
  }

  return (
    <div className="flex flex-col gap-3">
      <AmountField id="card-amount" balanceDue={amount} text={amountText} onChange={setAmountText} split={split} />
      <div ref={mountRef} data-testid="payment-element" className="min-h-[120px]" />
      {!ready && !loadError && <span className="text-[13px] text-gray-600">Loading the card form…</span>}
      {loadError && <span className={errorText} role="alert">{loadError}</span>}
      {canSaveCard && (
        <label className="flex items-center gap-2 text-sm text-gray-900">
          <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-floral-700 focus:ring-floral-700" />
          Save card for this customer
        </label>
      )}
      {error && <span className={errorText} role="alert">{error}</span>}
      <button type="button" onClick={pay} disabled={!ready || busy || !split.valid} className={`${btn.primary} w-full`}>
        <CreditCardIcon size={16} />{busy ? 'Charging…' : `Charge ${money(amountCents / 100)}`}
      </button>
    </div>
  );
}

// Send pay link: one tap texts or emails a link for the amount (the server
// makes the link too). Only the channels the order has are offered; with
// neither, the link is made to copy. Open links are listed below, each with
// Copy, Text, Email and Cancel, so a split (two links) is visible and
// fixable. The state's payLinks lists them (older servers: payLink only).
function PayLink({ orderId, state, notice, onChange, onNotification }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);
  const [copied, setCopied] = useState(null);
  // One action at a time, even for a double click that lands before the
  // buttons re-render as disabled. A second send within 30 seconds answers
  // 429 PAY_LINK_COOLDOWN; its message ("Sent 12 seconds ago. Try again in
  // 18 seconds.") shows like any other error.
  const inFlight = useRef(false);
  const links = state.payLinks || (state.payLink ? [state.payLink] : []);
  const covered = links.reduce((n, l) => n + cents(l.amount), 0);
  const dueCents = cents(state.balanceDue);
  // What the open links leave uncovered; the whole balance when they cover it all (a new link replaces them).
  const uncovered = covered > 0 && covered < dueCents ? (dueCents - covered) / 100 : null;
  const [amountText, setAmountText] = useAmountText(state.balanceDue, uncovered);
  const split = splitAmount(amountText, state.balanceDue);
  const noChannel = !state.hasPhone && !state.hasEmail;

  async function run(what, fn) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(what);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err.message || 'That did not work. Try again.');
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  }

  // channel for the typed amount (a new link, or the open one for that amount), or an open link again by id.
  const send = (channel, paymentId = null) => run(paymentId ? `${channel}-${paymentId}` : channel, async () => {
    const body = paymentId ? { channel, payment_id: paymentId } : { channel, ...(split.amount !== null ? { amount: split.amount } : {}) };
    const r = await orderPaymentsApi.sendPayLink(orderId, body);
    setSent(`${channel === 'sms' ? 'Texted' : 'Emailed'} to ${r.data.to}`);
    onNotification?.(channel === 'sms' ? 'Pay link texted' : 'Pay link emailed', 'success');
    await onChange();
  });
  const make = () => run('create', async () => { await orderPaymentsApi.payLink(orderId, split.amount); await onChange(); });
  const cancel = (id) => run(`cancel-${id}`, async () => { await orderPaymentsApi.cancelPayLink(orderId, id); await onChange(); });
  const copy = async (link) => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(link.id);
      setTimeout(() => setCopied(null), 2000);
    } catch (_) {
      setError('Copy did not work. Select the link and copy it.');
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {notice && <span className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[13px] text-gray-900">{notice}</span>}
      <AmountField id="pay-link-amount" balanceDue={state.balanceDue} text={amountText} onChange={setAmountText} split={split} />
      {noChannel && <span className="text-[13px] text-gray-600">No phone number or email on this order. Make the link and copy it.</span>}
      <div className="flex flex-wrap gap-2">
        {state.hasPhone && (
          <button type="button" onClick={() => send('sms')} disabled={!!busy || !split.valid} className={btn.primary}>
            <MessageSquareIcon size={14} />{busy === 'sms' ? 'Texting…' : 'Text pay link'}
          </button>
        )}
        {state.hasEmail && (
          <button type="button" onClick={() => send('email')} disabled={!!busy || !split.valid} className={state.hasPhone ? btn.secondary : btn.primary}>
            <MailIcon size={14} />{busy === 'email' ? 'Emailing…' : 'Email pay link'}
          </button>
        )}
        <button type="button" onClick={make} disabled={!!busy || !split.valid} className={noChannel ? btn.primary : btn.secondary}>
          <LinkIcon size={14} />{busy === 'create' ? 'Making the link…' : 'Make a link to copy'}
        </button>
      </div>
      {sent && <span className="text-[13px] text-ok-fg">{sent}</span>}
      {links.length > 0 && (
        <div className="flex flex-col gap-2" data-testid="open-pay-links">
          <span className="text-[13px] font-medium text-gray-900">{links.length === 1 ? 'Open pay link' : `Open pay links (${money(covered / 100)} of ${money(state.balanceDue)})`}</span>
          {links.map((link) => (
            <div key={link.id} className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="tabular-nums text-sm font-semibold text-gray-900">{money(link.amount)}</span>
                <input readOnly value={link.url} aria-label={`Pay link for ${money(link.amount)}`} className={`${inputClass} min-w-0 flex-1 text-[13px]`} onFocus={(e) => e.target.select()} />
                <button type="button" onClick={() => copy(link)} className={btn.secondary}>
                  {copied === link.id ? <CheckIcon size={14} /> : <CopyIcon size={14} />}{copied === link.id ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {state.hasPhone && (
                  <button type="button" onClick={() => send('sms', link.id)} disabled={!!busy} className={btn.ghost}>
                    <MessageSquareIcon size={13} />{busy === `sms-${link.id}` ? 'Texting…' : 'Text'}
                  </button>
                )}
                {state.hasEmail && (
                  <button type="button" onClick={() => send('email', link.id)} disabled={!!busy} className={btn.ghost}>
                    <MailIcon size={13} />{busy === `email-${link.id}` ? 'Emailing…' : 'Email'}
                  </button>
                )}
                <button type="button" onClick={() => cancel(link.id)} disabled={!!busy} className={`${btn.ghost} ml-auto`} aria-label={`Cancel the pay link for ${money(link.amount)}`}>
                  <XIcon size={13} />{busy === `cancel-${link.id}` ? 'Cancelling…' : 'Cancel'}
                </button>
              </div>
            </div>
          ))}
          <button type="button" onClick={() => run('refresh', onChange)} disabled={!!busy} className={`${btn.ghost} self-end`}>
            <RefreshCwIcon size={13} />Check payment
          </button>
        </div>
      )}
      {error && <span className={errorText} role="alert">{error}</span>}
    </div>
  );
}

function SavedCard({ orderId, state, onPaid, onFallback }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [amountText, setAmountText] = useAmountText(state.balanceDue);
  const split = splitAmount(amountText, state.balanceDue);
  async function charge() {
    if (!split.valid) return;
    setBusy(true);
    setError(null);
    try {
      const r = await orderPaymentsApi.chargeSavedCard(orderId, { attempt_key: newKey(), ...(split.amount !== null ? { amount: split.amount } : {}) });
      if (r.data.status === 'requires_action') onFallback();
      else await onPaid(r.data.paymentId, r.data.status === 'succeeded');
    } catch (err) {
      setError(err.message || 'The saved card was not charged.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm text-gray-900">Charge the card this customer saved: <strong>{state.savedCard.label}</strong></span>
      <AmountField id="saved-card-amount" balanceDue={state.balanceDue} text={amountText} onChange={setAmountText} split={split} />
      {error && <span className={errorText} role="alert">{error}</span>}
      <button type="button" onClick={charge} disabled={busy || !split.valid} className={`${btn.primary} w-full`}>
        <CreditCardIcon size={16} />{busy ? 'Charging…' : `Charge ${state.savedCard.label} ${money(split.cents / 100)}`}
      </button>
    </div>
  );
}

// How often the panel asks for the reader payment's status while it waits.
const READER_POLL_MS = 2000;

function ReaderPay({ orderId, readers, state, reload, onPaid, onNotification }) {
  const pending = state.readerPayment && state.readerPayment.status === 'pending' ? state.readerPayment : null;
  const [readerId, setReaderId] = useState(() => (pending && pending.readerId) || (readers.length === 1 ? readers[0].id : ''));
  // Waiting on { paymentId, readerId } once the reader has the payment.
  const [waitingOn, setWaitingOn] = useState(() => (pending ? { paymentId: pending.id, readerId: pending.readerId } : null));
  const [amountText, setAmountText] = useAmountText(state.balanceDue);
  const split = splitAmount(amountText, state.balanceDue);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [declined, setDeclined] = useState(null);
  const callbacks = useRef({ onPaid, onNotification });
  callbacks.current = { onPaid, onNotification };

  const reader = (id) => readers.find((r) => r.id === id) || null;
  const nameOf = (id) => (reader(id) && reader(id).label) || 'the reader';

  const paid = useCallback((paymentId) => {
    setWaitingOn(null);
    callbacks.current.onNotification?.('Payment received', 'success');
    callbacks.current.onPaid?.(paymentId);
  }, []);

  // While the reader waits for the card, poll the order's payment state until
  // Stripe's webhook has recorded the result.
  useEffect(() => {
    if (!waitingOn) return undefined;
    let stopped = false;
    let timer = null;
    const tick = async () => {
      try {
        const s = await reload();
        const mine = s && s.readerPayment && s.readerPayment.id === waitingOn.paymentId ? s.readerPayment : null;
        if ((s && (s.payments || []).some((p) => p.id === waitingOn.paymentId)) || (mine && mine.status === 'succeeded')) {
          paid(waitingOn.paymentId);
          return;
        }
        if (mine && mine.status === 'failed') {
          setDeclined(mine.failureText || 'The card was not charged.');
          setWaitingOn(null);
          return;
        }
        if (mine && mine.status === 'canceled') {
          setWaitingOn(null);
          return;
        }
      } catch (_) { /* try again */ }
      if (!stopped) timer = setTimeout(tick, READER_POLL_MS);
    };
    timer = setTimeout(tick, READER_POLL_MS);
    return () => { stopped = true; clearTimeout(timer); };
  }, [waitingOn, reload, paid]);

  async function charge() {
    if (!readerId || busy || !split.valid) return;
    setBusy(true);
    setError(null);
    setDeclined(null);
    try {
      const r = await orderPaymentsApi.chargeOnReader(orderId, { attempt_key: newKey(), reader_id: readerId, ...(split.amount !== null ? { amount: split.amount } : {}) });
      setWaitingOn({ paymentId: r.data.paymentId, readerId });
    } catch (err) {
      setError(err.message || 'The payment could not be sent to the reader.');
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!waitingOn || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await orderPaymentsApi.cancelReader(orderId, { payment_id: waitingOn.paymentId });
      if (r.data.status === 'succeeded') {
        await reload().catch(() => {});
        paid(waitingOn.paymentId);
      } else {
        setWaitingOn(null);
        await reload().catch(() => {});
      }
    } catch (err) {
      setError(err.message || 'The payment could not be cancelled.');
    } finally {
      setBusy(false);
    }
  }

  if (waitingOn) {
    return (
      <div className="flex flex-col gap-3">
        <span role="status" className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900">
          Waiting for card on {nameOf(waitingOn.readerId)}…
        </span>
        <span className="text-[13px] text-gray-600">The customer can tap, insert or swipe their card on the reader.</span>
        {error && <span className={errorText} role="alert">{error}</span>}
        <button type="button" onClick={cancel} disabled={busy} className={`${btn.secondary} self-start`}>
          <XIcon size={14} />{busy ? 'Cancelling…' : 'Cancel'}
        </button>
      </div>
    );
  }

  const chosen = reader(readerId);
  return (
    <div className="flex flex-col gap-3">
      {declined && (
        <span role="alert" className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[13px] text-gray-900">
          <span className="font-semibold text-danger-fg">Not charged.</span> {declined} Try again, or choose another way to pay.
        </span>
      )}
      <AmountField id="reader-amount" balanceDue={state.balanceDue} text={amountText} onChange={setAmountText} split={split} />
      {readers.length === 1 ? (
        <span className="text-sm text-gray-900">
          Reader: <strong>{readers[0].label}</strong>{readers[0].status === 'offline' ? ' (offline)' : ''}
        </span>
      ) : (
        <label className="flex flex-col gap-1 text-[13px] font-medium text-gray-900" htmlFor="payment-reader">
          Card reader
          <select id="payment-reader" value={readerId} onChange={(e) => setReaderId(e.target.value)} className={inputClass}>
            <option value="">Choose a reader</option>
            {readers.map((r) => (
              <option key={r.id} value={r.id} disabled={r.status === 'offline'}>
                {r.label}{r.status === 'offline' ? ' (offline)' : ''}
              </option>
            ))}
          </select>
        </label>
      )}
      {error && <span className={errorText} role="alert">{error}</span>}
      <button type="button" onClick={charge} disabled={!chosen || busy || !split.valid} className={`${btn.primary} w-full`}>
        <CreditCardIcon size={16} />
        {busy ? 'Sending to the reader…' : chosen ? `Charge ${money(split.cents / 100)} on ${chosen.label}` : 'Choose a reader'}
      </button>
    </div>
  );
}

function GiftCardPay({ orderId, state, onPaid, onNotification }) {
  const [code, setCode] = useState('');
  const [checked, setChecked] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [key, setKey] = useState(newKey);

  async function check(e) {
    if (e) e.preventDefault();
    if (!code.trim() || busy) return;
    setBusy('check');
    setError(null);
    setChecked(null);
    try {
      const r = await orderPaymentsApi.checkGiftCard(orderId, code.trim());
      setChecked(r.data);
    } catch (err) {
      setError(err.message || 'That gift card was not found or has no balance left.');
    } finally {
      setBusy(null);
    }
  }

  async function apply() {
    if (!checked || busy) return;
    setBusy('apply');
    setError(null);
    try {
      const r = await orderPaymentsApi.redeemGiftCard(orderId, { code: code.trim(), attempt_key: key });
      onNotification?.(`Gift card ··${r.data.last4}: ${money(r.data.applied)} applied`, 'success');
      setCode('');
      setChecked(null);
      setKey(newKey());
      await onPaid?.();
    } catch (err) {
      setError(err.message || 'The gift card was not applied.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <form onSubmit={check} className="flex items-end gap-2">
        <label className="flex min-w-0 flex-1 flex-col gap-1 text-[13px] font-medium text-gray-900" htmlFor="gift-card-code">
          Gift card code
          <input
            id="gift-card-code"
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setChecked(null); }}
            placeholder="XXXX-XXXX-XXXX-XXXX"
            className={`${inputClass} font-mono uppercase`}
          />
        </label>
        <button type="submit" disabled={!code.trim() || !!busy} className={btn.secondary}>
          {busy === 'check' ? 'Checking…' : 'Check balance'}
        </button>
      </form>
      {checked && (
        <span role="status" className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900">
          Gift card ··{checked.last4}: <strong>{money(checked.balance)}</strong> balance
          {checked.balance > checked.applies ? ` (${money(checked.balance - checked.applies)} stays on the card)` : ''}
        </span>
      )}
      {error && <span className={errorText} role="alert">{error}</span>}
      <button type="button" onClick={apply} disabled={!checked || !!busy} className={`${btn.primary} w-full`}>
        <GiftIcon size={16} />
        {busy === 'apply' ? 'Applying…' : checked ? `Apply ${money(checked.applies)} from the gift card` : `Check a gift card to pay up to ${money(state.balanceDue)}`}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cash, check and other (no card processor needed). Cash and check go on the
// register this device works on (Register screen); with none open the panel
// says "Open the register first".
// ---------------------------------------------------------------------------

/** The shop's registers that can take cash now, and the one this device uses. */
function useOpenRegister() {
  const [state, setState] = useState({ loading: true, open: [], error: null });
  const [chosen, setChosen] = useState(getDeviceRegisterId);
  const load = useCallback(async () => {
    try {
      const r = await registersApi.status();
      const open = ((r && r.data && r.data.registers) || []).filter((x) => x.active && x.openSession && !x.openSession.counted);
      setState({ loading: false, open, error: null });
    } catch (err) {
      setState({ loading: false, open: [], error: err.message || "Couldn't check the register." });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const register = state.open.find((x) => x.id === chosen) || (state.open.length === 1 ? state.open[0] : null);
  const choose = (id) => { setChosen(id); setDeviceRegisterId(id); };
  return { ...state, register, choose, reload: load };
}

function RegisterPicker({ reg }) {
  if (reg.loading) return <span className="text-[13px] text-gray-600">Checking the register…</span>;
  if (reg.error) return <span className={errorText} role="alert">{reg.error}</span>;
  if (reg.open.length === 0) {
    return (
      <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-900" role="status">
        <span><strong>Open the register first.</strong> Cash and checks go in the drawer, so a register must be open to take them.</span>
        <div className="flex flex-wrap gap-2">
          <Link to="/register" className={`${btn.primary}`}><LockOpenIcon size={16} />Open the register</Link>
          <button type="button" onClick={reg.reload} className={`${btn.secondary}`}><RefreshCwIcon size={14} />Check again</button>
        </div>
      </div>
    );
  }
  if (reg.open.length === 1 && reg.register) return <span className="text-[13px] text-gray-600">Into <strong>{reg.register.name}</strong></span>;
  return (
    <label className="flex flex-col gap-1 text-[13px] font-medium text-gray-900" htmlFor="payment-register">
      Register
      <select id="payment-register" value={reg.register ? reg.register.id : ''} onChange={(e) => reg.choose(e.target.value)} className={`${inputClass}`}>
        <option value="">Choose your register</option>
        {reg.open.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
      </select>
    </label>
  );
}

const bigInput = `${inputClass} h-ctl-lg text-[20px]`;
const parseDollars = (text) => {
  const v = String(text ?? '').replace(/[$,\s]/g, '');
  if (!v || !/^\d*(\.\d{0,2})?$/.test(v) || v === '.') return null;
  return Math.round(Number(v) * 100);
};

function CashPay({ orderId, state, onPaid }) {
  const reg = useOpenRegister();
  const [given, setGiven] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [key, setKey] = useState(newKey);
  const dueCents = cents(state.balanceDue);
  const givenCents = parseDollars(given);
  const change = givenCents !== null ? givenCents - dueCents : null;

  async function take(e) {
    e.preventDefault();
    if (!givenCents || !reg.register || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await orderPaymentsApi.cash(orderId, { attempt_key: key, tendered: givenCents / 100, register_id: reg.register.id });
      setGiven('');
      setKey(newKey());
      await onPaid(r.data);
    } catch (err) {
      setError(err.message || 'The cash payment was not recorded.');
      if (err?.data?.errorCode && /^REGISTER_/.test(err.data.errorCode)) reg.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={take} className="flex flex-col gap-3">
      <RegisterPicker reg={reg} />
      {reg.register && (
        <>
          <div className="flex items-end gap-2">
            <label className="flex min-w-0 flex-1 flex-col gap-1 text-[13px] font-medium text-gray-900" htmlFor="cash-given">
              Cash from the customer
              <input id="cash-given" inputMode="decimal" autoComplete="off" value={given} onChange={(e) => setGiven(e.target.value)} placeholder="0.00" className={bigInput} />
            </label>
            <button type="button" onClick={() => setGiven((dueCents / 100).toFixed(2))} className={`${btn.secondary} h-ctl-lg`}>Exact {money(state.balanceDue)}</button>
          </div>
          {givenCents !== null && givenCents > 0 && (
            <div className="flex items-baseline justify-between rounded-md border border-gray-200 bg-gray-50 px-4 py-3" data-testid="change-due">
              <span className="text-sm text-gray-600">{change >= 0 ? 'Change due' : 'Still due after this'}</span>
              <span className="text-[32px] font-semibold leading-none tracking-tight tabular-nums text-gray-900">{money(Math.abs(change) / 100)}</span>
            </div>
          )}
          {error && <span className={errorText} role="alert">{error}</span>}
          <button type="submit" disabled={!givenCents || busy} className={`${btn.primary} h-ctl-lg w-full`}>
            <BanknoteIcon size={16} />{busy ? 'Recording…' : `Take ${money(Math.min(givenCents || 0, dueCents) / 100)} cash`}
          </button>
        </>
      )}
    </form>
  );
}

function CheckPay({ orderId, state, onPaid, onNotification }) {
  const reg = useOpenRegister();
  const [number, setNumber] = useState('');
  const [amount, setAmount] = useState(Number(state.balanceDue).toFixed(2));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [key, setKey] = useState(newKey);
  const amountCents = parseDollars(amount);
  const valid = number.trim() && amountCents && amountCents > 0 && amountCents <= cents(state.balanceDue);

  async function take(e) {
    e.preventDefault();
    if (!valid || !reg.register || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await orderPaymentsApi.check(orderId, { attempt_key: key, check_number: number.trim(), amount: amountCents / 100, register_id: reg.register.id });
      onNotification?.(`Check #${r.data.checkNumber}: ${money(r.data.applied)} recorded`, 'success');
      setNumber('');
      setKey(newKey());
      await onPaid(r.data);
    } catch (err) {
      setError(err.message || 'The check was not recorded.');
      if (err?.data?.errorCode && /^REGISTER_/.test(err.data.errorCode)) reg.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={take} className="flex flex-col gap-3">
      <RegisterPicker reg={reg} />
      {reg.register && (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-[13px] font-medium text-gray-900" htmlFor="check-number">
              Check number
              <input id="check-number" inputMode="numeric" autoComplete="off" maxLength={40} value={number} onChange={(e) => setNumber(e.target.value)} className={`${inputClass} h-ctl-lg text-[17px]`} />
            </label>
            <label className="flex flex-col gap-1 text-[13px] font-medium text-gray-900" htmlFor="check-amount">
              Amount (up to {money(state.balanceDue)})
              <input id="check-amount" inputMode="decimal" autoComplete="off" value={amount} onChange={(e) => setAmount(e.target.value)} className={`${inputClass} h-ctl-lg text-[17px]`} />
            </label>
          </div>
          <span className="text-[13px] text-gray-600">Checks go in the deposit, not the cash count.</span>
          {error && <span className={errorText} role="alert">{error}</span>}
          <button type="submit" disabled={!valid || busy} className={`${btn.primary} h-ctl-lg w-full`}>
            <ReceiptIcon size={16} />{busy ? 'Recording…' : `Take check for ${money((amountCents || 0) / 100)}`}
          </button>
        </>
      )}
    </form>
  );
}

function OtherPay({ orderId, state, onPaid, onNotification }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [key, setKey] = useState(newKey);
  async function mark(e) {
    e.preventDefault();
    if (reason.trim().length < 3 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await orderPaymentsApi.other(orderId, { attempt_key: key, reason: reason.trim() });
      onNotification?.(`${money(r.data.applied)} marked paid`, 'success');
      setReason('');
      setKey(newKey());
      await onPaid();
    } catch (err) {
      setError(err.message || 'The order was not marked paid.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={mark} className="flex flex-col gap-3">
      <span className="text-[13px] text-gray-600">For money taken outside the app, like an invoice or a bank transfer. Nothing goes in the register.</span>
      <label className="flex flex-col gap-1 text-[13px] font-medium text-gray-900" htmlFor="other-reason">
        How was it paid?
        <input id="other-reason" type="text" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Paid by invoice #1042" className={`${inputClass}`} />
      </label>
      {error && <span className={errorText} role="alert">{error}</span>}
      <button type="submit" disabled={reason.trim().length < 3 || busy} className={`${btn.primary} w-full`}>
        <CheckIcon size={16} />{busy ? 'Saving…' : `Mark ${money(state.balanceDue)} paid`}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Receipts (REG2): "Print receipt" / "No receipt" after a sale, Reprint receipt
// ---------------------------------------------------------------------------

const RECEIPT_NOT_SENT = {
  RECEIPT_PRINTER_OFFLINE: 'The receipt printer is offline. Check that the print client is running.',
  RECEIPTS_OFF: 'Receipts for card and check sales are turned off in Settings.',
};

/**
 * After a sale: Print receipt or No receipt. The choice this device made
 * last comes first (and is remembered again). Calls onDone once the
 * receipt is on its way or staff chose none.
 */
function ReceiptChoice({ orderId, paymentId, status, onDone, onNotification }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [printFirst] = useState(() => getReceiptChoice() !== 'none');
  const offline = !status.online;

  async function print() {
    setReceiptChoice('print');
    setBusy(true);
    setError(null);
    try {
      const r = await orderPaymentsApi.receipt(orderId, { payment_id: paymentId, reason: 'sale', register_id: getDeviceRegisterId() || undefined });
      if (r && r.data && r.data.sent) {
        onNotification?.('Receipt sent to the printer', 'success');
        onDone();
        return;
      }
      setError(RECEIPT_NOT_SENT[r && r.data && r.data.reason] || 'The receipt did not print.');
    } catch (err) {
      setError(err.message || 'The receipt did not print.');
    } finally {
      setBusy(false);
    }
  }
  function none() {
    // A receipt that failed to print doesn't make "No receipt" this device's habit.
    if (!error) setReceiptChoice('none');
    onDone();
  }

  const printButton = (
    <button key="print" type="button" onClick={print} disabled={busy || offline} className={`${printFirst ? btn.primary : btn.secondary} h-ctl-lg w-full`}>
      <PrinterIcon size={16} />{busy ? 'Printing…' : 'Print receipt'}
    </button>
  );
  const noneButton = (
    <button key="none" type="button" onClick={none} disabled={busy} className={`${printFirst ? btn.secondary : btn.primary} h-ctl-lg w-full`}>
      No receipt
    </button>
  );
  return (
    <div className="flex flex-col gap-2" data-testid="receipt-choice">
      <ReceiptPrinterStatus status={status} />
      {error && <span className={errorText} role="alert">{error}</span>}
      <div className="grid grid-cols-2 gap-2">
        {printFirst ? [printButton, noneButton] : [noneButton, printButton]}
      </div>
    </div>
  );
}

function ReprintReceipt({ orderId, status, onNotification }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  async function reprint() {
    setBusy(true);
    setMessage(null);
    try {
      const r = await orderPaymentsApi.receipt(orderId, { reason: 'reprint', register_id: getDeviceRegisterId() || undefined });
      if (r && r.data && r.data.sent) {
        onNotification?.('Receipt sent to the printer', 'success');
        setMessage({ ok: true, text: 'Receipt sent to the printer.' });
      } else {
        setMessage({ ok: false, text: RECEIPT_NOT_SENT[r && r.data && r.data.reason] || 'The receipt did not print.' });
      }
    } catch (err) {
      setMessage({ ok: false, text: err.message || 'The receipt did not print.' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button type="button" onClick={reprint} disabled={busy || !status.online} className="btn-secondary btn-sm">
        <PrinterIcon size={14} />{busy ? 'Printing…' : 'Reprint receipt'}
      </button>
      {message && <span className={message.ok ? 'text-[13px] text-gray-600' : errorText} role={message.ok ? 'status' : 'alert'}>{message.text}</span>}
    </>
  );
}

const STOREFRONT_STATUS = {
  paid: 'Paid',
  partially_paid: 'Partly paid',
  partially_refunded: 'Partly refunded',
  refunded: 'Refunded',
  pending: 'Waiting for payment',
  unpaid: 'Waiting for payment',
};

function StorefrontPayment({ info }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const rate = Number(info.taxRate);
  const taxLabel = [Number.isFinite(rate) && rate > 0 ? `${+(rate * 100).toFixed(3)}%` : null, info.taxSource].filter(Boolean).join(', ');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(info.paymentIntentId);
      setCopied(true);
      setCopyError(false);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      setCopyError(true);
    }
  };
  const row = (label, value) => (
    <div className="flex flex-wrap items-baseline justify-between gap-x-2">
      <span className="text-gray-600">{label}</span>
      <span className="text-gray-900">{value}</span>
    </div>
  );
  return (
    <div className="flex flex-col gap-1.5 text-sm" data-testid="storefront-payment">
      <span className={labelClass}>Online store payment</span>
      {row('Status', STOREFRONT_STATUS[info.paymentStatus] || (info.paymentStatus || '').replace(/_/g, ' '))}
      {info.discount > 0 && row('Discount', `−${money(info.discount)}`)}
      {info.giftCard > 0 && row(info.giftCardLabel || 'Gift card', money(info.giftCard))}
      {row('Total paid', money(info.totalPaid))}
      {info.refunded > 0 && row('Refunded', money(info.refunded))}
      {row(`Tax${taxLabel ? ` (${taxLabel})` : ''}`, money(info.tax))}
      {info.tip > 0 && row('Tip', money(info.tip))}
      {info.card && row('Card', info.card)}
      {info.paymentIntentId && (
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <span className="text-gray-600">Stripe payment</span>
          <span className="flex items-center gap-1.5">
            <code className="break-all text-[12px] text-gray-900">{info.paymentIntentId}</code>
            <button type="button" onClick={copy} className={btn.ghost} aria-label="Copy the Stripe payment id">
              {copied ? <CheckIcon size={13} /> : <CopyIcon size={13} />}{copied ? 'Copied' : 'Copy'}
            </button>
          </span>
        </div>
      )}
      {copyError && <span className={errorText} role="alert">Copy did not work. Select the id and copy it.</span>}
    </div>
  );
}

function RefundForm({ orderId, refundable, hasGiftCard = false, hasCash = false, onDone, onCancel, onNotification }) {
  const [amount, setAmount] = useState(refundable.toFixed(2));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [key] = useState(newKey);
  const value = Number(amount);
  const valid = Number.isFinite(value) && value > 0 && cents(value) <= cents(refundable);

  async function submit(e) {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const r = await orderPaymentsApi.refund(orderId, { refund_key: key, amount: value, reason: reason.trim() || undefined, register_id: getDeviceRegisterId() || undefined });
      onNotification?.(`Refunded ${money(r.data.refunded)}`, 'success');
      await onDone();
    } catch (err) {
      setError(err.message || 'The refund did not go through.');
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-2.5 rounded-md border border-gray-200 bg-gray-50 p-3.5">
      <label className="flex flex-col gap-1 text-[13px] font-medium text-gray-900" htmlFor="refund-amount">
        Amount to refund (up to {money(refundable)})
        <input id="refund-amount" type="number" min="0.01" step="0.01" max={refundable} value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} />
      </label>
      {hasGiftCard && !hasCash && <span className="text-[13px] text-gray-600">Card payments are refunded first; the rest goes back on the gift card.</span>}
      {hasCash && <span className="text-[13px] text-gray-600">Card payments are refunded first, then cash (from the open register) and checks{hasGiftCard ? ', then the gift card' : ''}.</span>}
      <label className="flex flex-col gap-1 text-[13px] font-medium text-gray-900" htmlFor="refund-reason">
        Reason (optional)
        <input id="refund-reason" type="text" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} />
      </label>
      {error && <span className={errorText} role="alert">{error}</span>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className={btn.ghost}>Cancel</button>
        <button type="submit" disabled={!valid || busy} className={btn.primary}>
          <RotateCcwIcon size={14} />{busy ? 'Refunding…' : `Refund ${valid ? money(value) : ''}`.trim()}
        </button>
      </div>
    </form>
  );
}

const OrderPaymentPanel = ({ orderId, isAdmin = false, onPaid, onNotification, onState, className = '' }) => {
  // onState(state): every payment state the panel loads (balance, payments),
  // for a host that words itself by it (the order form's success dialog).
  // Held in a ref so a new function each render never reloads the panel.
  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  const [config, setConfig] = useState(null);
  const [state, setState] = useState(null);
  const [tab, setTab] = useState('card');
  const [notice, setNotice] = useState(null);
  const [waiting, setWaiting] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [readers, setReaders] = useState([]);
  // The last cash payment: its change due stays on screen until Done (or,
  // with a receipt printer, until Print receipt / No receipt).
  const [cashDone, setCashDone] = useState(null);
  // A card or check sale waiting on Print receipt / No receipt: { paymentId, title }.
  const [receiptFor, setReceiptFor] = useState(null);
  // Gift cards come with the online store; a shop known not to have it gets no tab.
  const noGiftCards = useLacksEntitlement('online_store');
  // The receipt printer (only asked for shops with the cash register on).
  const receiptStatus = useReceiptPrinterStatus(Boolean(state && state.tenders && state.tenders.cash));
  const alive = useRef(true);

  // Set on every mount: StrictMode mounts, unmounts and mounts again in development.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const reload = useCallback(async () => {
    const r = await orderPaymentsApi.get(orderId);
    if (alive.current) {
      setState(r.data);
      onStateRef.current?.(r.data);
    }
    return r.data;
  }, [orderId]);

  // Whether to ask Print receipt / No receipt after a sale: the shop has a
  // receipt printer; cash always, card and check while print_receipts is on.
  const offerReceipt = (kind) => Boolean(receiptStatus.setUp && state && state.tenders && state.tenders.cash
    && (kind === 'cash' || state.printReceipts));
  const offerReceiptRef = useRef(offerReceipt);
  offerReceiptRef.current = offerReceipt;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const c = await loadOrderPaymentConfig();
      if (cancelled) return;
      setConfig(c);
      // Shopify shops (Twigs) take payment in Shopify: nothing more is asked.
      if (c.provider === 'shopify' || !orderId) return;
      let s = null;
      try { s = await reload(); } catch (_) { /* the panel stays hidden */ }
      if (cancelled || !s || s.provider !== 'stripe' || (!s.canTakePayment && !(s.readerPayment && s.readerPayment.status === 'pending'))) return;
      // The shop's card readers (403 without counter_payments, or none
      // registered: no reader option).
      try {
        const r = await cardReadersApi.list();
        if (cancelled) return;
        const list = (r && r.data && r.data.readers) || [];
        setReaders(list);
        if (list.length > 0 && s.readerPayment && s.readerPayment.status === 'pending') setTab('reader');
      } catch (_) { /* no reader option */ }
    })();
    return () => { cancelled = true; };
  }, [orderId, reload]);

  // After a card is confirmed, wait for Stripe's webhook to record it.
  const waitForPayment = useCallback(async (paymentId, recordedAlready = false) => {
    setWaiting(true);
    setNotice(null);
    let found = false;
    for (let i = 0; i < (recordedAlready ? 1 : 15) && alive.current; i += 1) {
      if (i > 0) await new Promise((r) => setTimeout(r, 2000));
      try {
        const s = await reload();
        if (s.payments && s.payments.some((p) => p.id === paymentId)) { found = true; break; }
      } catch (_) { /* try again */ }
    }
    if (!alive.current) return;
    setWaiting(false);
    if (found) {
      onNotification?.('Payment received', 'success');
      if (offerReceiptRef.current('card')) setReceiptFor({ paymentId, title: 'Payment received.' });
      else onPaid?.();
    } else {
      setNotice('Stripe accepted the card. The payment shows here as soon as Stripe confirms it.');
    }
  }, [reload, onPaid, onNotification]);

  if (!config || config.provider === 'shopify' || !state || state.provider === 'shopify') return null;
  const payments = state.payments || [];
  const stripe = state.provider === 'stripe';
  // Which ways to pay this shop has (an older server knew only Stripe cards).
  const tenders = state.tenders || { card: stripe };
  const savedLabel = state.savedCard ? state.savedCard.label : null;
  const hasReaders = readers.length > 0;
  const tabs = [
    tenders.card && { key: 'card', label: 'Enter card' },
    tenders.card && { key: 'link', label: 'Send pay link' },
    tenders.card && savedLabel && { key: 'saved', label: `Charge saved card (${savedLabel})` },
    tenders.card && hasReaders && { key: 'reader', label: 'Charge on reader' },
    tenders.cash && { key: 'cash', label: 'Cash' },
    tenders.check && { key: 'check', label: 'Check' },
    !noGiftCards && { key: 'gift', label: 'Gift card' },
    tenders.other && { key: 'other', label: 'Other' },
  ].filter(Boolean);
  const canTake = state.canTakePayment && tabs.length > 0;
  if (!canTake && payments.length === 0 && !state.storefront && !state.checkoutInProgress && !cashDone && !receiptFor) return null;

  const refundable = payments.reduce((n, p) => n + Number(p.refundable || 0), 0);
  const activeTab = tabs.some((t) => t.key === tab) ? tab : (tabs[0] && tabs[0].key);
  const hasGiftCardPayment = payments.some((p) => p.method === 'gift_card');
  const hasCashPayment = payments.some((p) => p.method === 'cash' || p.method === 'check');
  const onlyCards = payments.every((p) => !['gift_card', 'cash', 'check', 'other'].includes(p.method));
  const afterPayment = async () => { const s = await reload(); if (s && !s.canTakePayment) onPaid?.(); };
  const finishReceipt = async () => { setReceiptFor(null); await afterPayment(); };
  const showReceiptChoice = cashDone && offerReceipt('cash');

  return (
    <section className={`card ${className}`} aria-label="Take payment">
      <div className="card-header px-[18px]">
        <span className="card-title">{canTake || state.checkoutInProgress ? 'Take payment' : onlyCards ? 'Card payments' : 'Payments'}</span>
        {canTake && <span className="tabular text-sm font-semibold text-prog-fg">{money(state.balanceDue)} due</span>}
      </div>
      <div className="flex flex-col gap-3.5 px-[18px] py-4">
      {cashDone && (
        <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 px-4 py-3.5" role="status" data-testid="cash-done">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-gray-600">Change due</span>
            <span className="text-[40px] font-semibold leading-none tracking-tight tabular-nums text-gray-900">{money(cashDone.change)}</span>
          </div>
          <span className="text-[13px] text-gray-600">
            {money(cashDone.applied)} paid in cash ({money(cashDone.tendered)} given){cashDone.balanceDue > 0 ? `. ${money(cashDone.balanceDue)} still due.` : '.'}
          </span>
          {receiptStatus.setUp && cashDone.drawer && !cashDone.drawer.sent && (
            <span className={errorText} role="alert">The cash drawer is offline, so it didn't open.</span>
          )}
          {showReceiptChoice ? (
            <ReceiptChoice orderId={orderId} paymentId={cashDone.paymentId} status={receiptStatus} onNotification={onNotification} onDone={() => { setCashDone(null); onPaid?.(); }} />
          ) : (
            <button type="button" onClick={() => { setCashDone(null); onPaid?.(); }} className={`${btn.primary} h-ctl-lg w-full`}>Done</button>
          )}
        </div>
      )}
      {receiptFor && (
        <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 px-4 py-3.5" role="status" data-testid="receipt-for">
          <span className="text-sm text-gray-900">{receiptFor.title}</span>
          <ReceiptChoice orderId={orderId} paymentId={receiptFor.paymentId} status={receiptStatus} onNotification={onNotification} onDone={finishReceipt} />
        </div>
      )}
      {canTake && state.paid > 0 && (
        <span className="text-[13px] text-gray-600">{money(state.paid)} paid of {money(state.total)}</span>
      )}

      {/* The customer is paying online right now (4E): no charge options until it finishes or expires. */}
      {state.checkoutInProgress && (
        <span role="status" data-testid="checkout-in-progress" className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[13px] text-gray-900">
          {state.checkoutInProgressMessage || 'The customer is paying online right now. Wait for it to finish or expire (30 minutes).'}
        </span>
      )}

      {state.storefront && <StorefrontPayment info={state.storefront} />}

      {canTake && !cashDone && !receiptFor && (
        <>
          <div role="tablist" className="seg flex flex-wrap">
            {tabs.map((t) => (
              <button key={t.key} type="button" role="tab" aria-selected={activeTab === t.key} onClick={() => setTab(t.key)} className={tabClass(activeTab === t.key)}>{t.label}</button>
            ))}
          </div>
          {waiting ? (
            <span className="text-sm text-gray-900">Confirming the payment with Stripe…</span>
          ) : (
            <>
              {activeTab === 'card' && (
                <EnterCard orderId={orderId} config={config} amount={state.balanceDue} canSaveCard={state.canSaveCard} onConfirmed={(id) => waitForPayment(id)} />
              )}
              {activeTab === 'link' && (
                <PayLink orderId={orderId} state={state} notice={notice} onChange={async () => { const s = await reload(); if (!s.canTakePayment) onPaid?.(); }} onNotification={onNotification} />
              )}
              {activeTab === 'saved' && (
                <SavedCard
                  orderId={orderId}
                  state={state}
                  onPaid={(id, recorded) => waitForPayment(id, recorded)}
                  onFallback={async () => {
                    setTab('link');
                    await reload();
                    setNotice('The bank wants the customer to confirm this charge. Send them the pay link.');
                  }}
                />
              )}
              {activeTab === 'reader' && (
                <ReaderPay
                  orderId={orderId}
                  readers={readers}
                  state={state}
                  reload={reload}
                  onPaid={(paymentId) => {
                    if (paymentId && offerReceipt('card')) setReceiptFor({ paymentId, title: 'Payment received.' });
                    else onPaid?.();
                  }}
                  onNotification={onNotification}
                />
              )}
              {activeTab === 'gift' && (
                <GiftCardPay
                  orderId={orderId}
                  state={state}
                  onNotification={onNotification}
                  onPaid={afterPayment}
                />
              )}
              {activeTab === 'cash' && (
                <CashPay
                  orderId={orderId}
                  state={state}
                  onPaid={async (result) => { setCashDone(result); await reload(); }}
                />
              )}
              {activeTab === 'check' && (
                <CheckPay
                  orderId={orderId}
                  state={state}
                  onNotification={onNotification}
                  onPaid={async (result) => {
                    if (result && result.paymentId && offerReceipt('check')) {
                      await reload();
                      setReceiptFor({ paymentId: result.paymentId, title: `Check #${result.checkNumber}: ${money(result.applied)} recorded.` });
                    } else {
                      await afterPayment();
                    }
                  }}
                />
              )}
              {activeTab === 'other' && <OtherPay orderId={orderId} state={state} onNotification={onNotification} onPaid={afterPayment} />}
              {notice && activeTab !== 'link' && <span className="text-[13px] text-gray-600">{notice}</span>}
            </>
          )}
        </>
      )}

      {payments.length > 0 && (
        <div className={`flex flex-col gap-2 text-[13px] ${canTake || state.storefront || cashDone || receiptFor ? 'border-t border-gray-200 pt-3' : ''}`}>
          {canTake && <span className={labelClass}>{onlyCards ? 'Card payments' : 'Payments'}</span>}
          {payments.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-x-2">
              <PaymentLabel label={p.card} when={p.paidAt ? ` · ${formatDateInTimezone(p.paidAt, { month: 'short', day: 'numeric' })}` : ''} />
              <span className="tabular font-semibold text-gray-900">
                {money(p.amount)}
                {p.amountRefunded > 0 && <span className="font-normal text-gray-500"> · {money(p.amountRefunded)} refunded</span>}
              </span>
            </div>
          ))}
          {((isAdmin && refundable > 0 && !refunding) || (receiptStatus.setUp && tenders.cash)) && (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {isAdmin && refundable > 0 && !refunding && (
                <button type="button" onClick={() => setRefunding(true)} className="btn-secondary btn-sm">
                  Refund…
                </button>
              )}
              {receiptStatus.setUp && tenders.cash && <ReprintReceipt orderId={orderId} status={receiptStatus} onNotification={onNotification} />}
            </div>
          )}
          {isAdmin && refunding && (
            <RefundForm
              orderId={orderId}
              refundable={refundable}
              hasGiftCard={hasGiftCardPayment}
              hasCash={hasCashPayment}
              onNotification={onNotification}
              onCancel={() => setRefunding(false)}
              onDone={async () => { setRefunding(false); await reload(); onPaid?.(); }}
            />
          )}
        </div>
      )}
      </div>
    </section>
  );
};

export default OrderPaymentPanel;
